import {
  configureStore,
  ThunkAction,
  Action,
  PreloadedState,
  combineReducers,
} from "@reduxjs/toolkit";
import { rememberReducer, rememberEnhancer } from "redux-remember";
import formsReducer from "src/forms/formsSlice";
import appReducer from "src/app/appSlice";
import authReducer from "src/auth/authSlice";
import wizardReducer from "src/components/wizard/wizardSlice";
import reviewPageReducer from "src/forms/reviewPage/reviewPageSlice";
import sitesReducer from "src/sites/sitesSlice";
import workersCompensationDocumentsReducer from "src/views/workersCompensationDocuments/workersCompensationDocumentsSlice";
import cargoAppReducer from "src/app/Cargo/cargoAppSlice";
import iplAppReducer from "src/app/IPL/iplAppSlice";
import jsonFormsReducer from "src/jsonForms/jsonFormsSlice";
import {
  preScreenPersistenceSerialize,
  preScreenPersistenceUnserialize,
} from "./preScreenPersistence";

export const rootReducer = combineReducers({
  forms: formsReducer,
  app: appReducer,
  auth: authReducer,
  wizard: wizardReducer,
  reviewPage: reviewPageReducer,
  sites: sitesReducer,
  workersCompensationDocuments: workersCompensationDocumentsReducer,
  cargoApp: cargoAppReducer,
  iplApp: iplAppReducer,
  jsonForms: jsonFormsReducer,
});

// Don't remember the auth state to force new authentication on every login.
// `jsonForms` is included so the pre-screen subset survives a refresh; the
// custom `serialize`/`unserialize` strip the ephemeral and non-preScreen keys
// so only `preScreen.wc`, `preScreen.cargo`, `preScreen.ipl`, and
// `preScreen.resolvedSchemaName` are persisted.
const rememberedKeys = ["forms", "wizard", "reviewPage", "sites", "jsonForms"];
const persistedReducer = rememberReducer(rootReducer);
const enhancers = [
  rememberEnhancer(window.localStorage, rememberedKeys, {
    persistWholeStore: true,
    serialize: preScreenPersistenceSerialize,
    unserialize: preScreenPersistenceUnserialize,
  }),
];
export function setupStore(
  preloadedState?: PreloadedState<RootState>,
  enhancers?: Parameters<typeof configureStore>[0]["enhancers"]
) {
  return configureStore({
    reducer: persistedReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      /**
       * Disable middleware causing slowdowns and warnings in dev environment, this does
       * not affect production as these middlewares are disabled by default in production.
       * https://redux-toolkit.js.org/api/getDefaultMiddleware
       */
      getDefaultMiddleware({
        serializableCheck: false,
        immutableCheck: false,
      }),
    enhancers,
  });
}
export const store = setupStore({}, enhancers);
export type RootState = ReturnType<typeof persistedReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore["dispatch"];
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
